import type { DateTime } from "luxon";
import { createContext, useContext } from "react";
import type {
	ScheduleChangeAction,
	ScheduleChangeActorKind,
	ScheduleChangesFeed,
	ScheduleChangesWindowId,
	ScheduleChangesWindowPreset,
} from "@/components/scheduleChanges/types";

/** The window the Changes tab lists, and the presets its filters offer. */
export type ScheduleChangesActiveWindow = {
	activeId: ScheduleChangesWindowId;
	/**
	 * Where the changes shown start — what every "since …" caption names. The active preset's start, or later when the
	 * window has grown past what the server answers (a tab left open for weeks) and the request was trimmed. Its end is
	 * always "now".
	 */
	since: DateTime;
	/** The active preset's own start, untrimmed and stable for the session — what a query keys on. */
	requestedSince: DateTime;
	/** The visit this session started from, or null on a first visit from this device. */
	lastVisit: DateTime | null;
	presets: readonly ScheduleChangesWindowPreset[];
	setActiveId: (id: ScheduleChangesWindowId) => void;
};

/**
 * Where the feed stands: `idle` (inert, or no department yet), `loading` (first load of this window), `error` (that
 * load failed — nothing is known) or `ready` (loaded; a later failed poll keeps the last answer and sets `isError`).
 */
export type ScheduleChangesFeedStatus = "idle" | "loading" | "error" | "ready";

/** The one feed query the side panel's Changes tab (and its unseen dot) reads. */
export type ScheduleChangesFeedState = {
	/** The window's feed, unfiltered. Null unless `status` is `ready`. */
	data: ScheduleChangesFeed | null;
	status: ScheduleChangesFeedStatus;
	isLoading: boolean;
	isError: boolean;
	error: Error | null;
	refetch: () => void;
};

export type ScheduleChangesContextValue = {
	/**
	 * False for staff users and before a department has loaded. Everything below is then empty and inert (no request
	 * is sent), so a surface shared by staff and admins can call `useScheduleChanges()` unconditionally.
	 */
	isEnabled: boolean;
	/** Null while inert. */
	changesWindow: ScheduleChangesActiveWindow | null;
	feed: ScheduleChangesFeedState;
	/** Whose changes the Changes tab lists — By me / By others / By system, everyone on a new session. Never empty. Kept for the session. */
	whoFilter: readonly ScheduleChangeActorKind[];
	setWhoFilter: (next: readonly ScheduleChangeActorKind[]) => void;
	/** "What changed": the kinds of change the Changes tab lists. Never empty. Kept for the session. */
	actionFilter: readonly ScheduleChangeAction[];
	setActionFilter: (next: readonly ScheduleChangeAction[]) => void;
	/**
	 * Counts a surface that shows the changes until the returned cleanup runs; the feed fetches and polls only while one
	 * is counted. Call it through `useScheduleChangesFeedConsumer`.
	 */
	registerFeedConsumer: () => () => void;
};

export const ScheduleChangesContext = createContext<ScheduleChangesContextValue | null>(null);

/** The shared schedule-changes state. Must be rendered under `ScheduleChangesProvider` (mounted for every logged-in route). */
export const useScheduleChanges = (): ScheduleChangesContextValue => {
	const context = useContext(ScheduleChangesContext);
	if (!context) throw new Error("useScheduleChanges must be used within a ScheduleChangesProvider");
	return context;
};
